import { useCallback, useEffect, useLayoutEffect, useId, useMemo, useRef, useState } from 'react';
import { CheckRounded, Close, FormatAlignLeftRounded, IosShareRounded, Pause, PlayArrow, StarRounded, SwapVert } from '@mui/icons-material';
import { getAnalytics, logEvent } from 'firebase/analytics';

import FullWidthButton from './FullWidthButton';
import StatusToast from './StatusToast';
import { getSectionTitle } from './sectionHelper';
import { fancyTimeFormat } from './timeHelper';
import { LABEL_DOMAIN } from '../constants/labels';
import { shareContent, SHARE_FAILURE_MESSAGE } from '../utils/shareContent';
import { eventLog } from './eventHelper';
import './Sections.css';

const EMPTY_SECTIONS = [];

const formatSectionDuration = duration => {
    const seconds = Number.isFinite(duration) ? Math.max(0, Math.floor(duration)) : 0;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds % 3600 / 60);
    const remainder = seconds % 60;
    return [hours && `${hours}h`, minutes && `${minutes}m`, remainder && `${remainder}s`]
        .filter(Boolean).join(' ') || '0s';
};

const Sections = ({ playing, close, currentSection, curTime = 0, sectionsContainerRef, track, onSeek, onTogglePlayback }) => {
    const descriptionPrefix = useId();
    const audio = document.getElementById('audio');
    const sections = track?.sectionInfos ?? EMPTY_SECTIONS;
    const [showSummary, setShowSummary] = useState(false);
    const [showCurrentSectionButton, setShowCurrentSectionButton] = useState(false);
    const [copiedToastGeneration, setCopiedToastGeneration] = useState(0);
    const [shareError, setShareError] = useState('');
    const [playbackRate, setPlaybackRate] = useState(audio?.playbackRate || 1);
    const rows = useRef([]);
    const following = useRef(true);
    const programmaticScroll = useRef(false);
    const scrollTimer = useRef(null);
    const summaryAnchor = useRef(null);

    const popularSections = useMemo(() => {
        const totalMessages = track?.transcript?.length || 1;
        return new Set(sections
            .map((section, index) => ({
                index,
                score: section.score,
                // Prefer shorter sections when relevance scores are close.
                rank: section.score + (1 - section.numMessages / totalMessages) / 100,
            }))
            .filter(section => section.score > 0.5)
            .sort((a, b) => b.rank - a.rank)
            .slice(0, Math.floor(sections.length * 0.25))
            .map(section => section.index));
    }, [sections, track?.transcript?.length]);

    const updateCurrentVisibility = useCallback(() => {
        const container = sectionsContainerRef.current;
        const row = rows.current[currentSection];
        if (!container || !row) {
            setShowCurrentSectionButton(false);
            return;
        }
        const viewport = container.getBoundingClientRect();
        const bounds = row.getBoundingClientRect();
        setShowCurrentSectionButton(!(bounds.bottom > viewport.top + 12 && bounds.top < viewport.bottom - 36));
    }, [currentSection, sectionsContainerRef]);

    const scheduleScrollEnd = useCallback(() => {
        clearTimeout(scrollTimer.current);
        scrollTimer.current = setTimeout(() => {
            programmaticScroll.current = false;
            updateCurrentVisibility();
        }, 200);
    }, [updateCurrentVisibility]);

    const scrollToCurrent = useCallback((smooth = true) => {
        const container = sectionsContainerRef.current;
        const row = rows.current[currentSection];
        if (!container || !row) return;
        programmaticScroll.current = true;
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        container.scrollTo({
            top: Math.max(0, container.scrollTop + row.getBoundingClientRect().top - container.getBoundingClientRect().top - 12),
            behavior: smooth && !reducedMotion ? 'smooth' : 'auto',
        });
        updateCurrentVisibility();
        scheduleScrollEnd();
    }, [currentSection, sectionsContainerRef, scheduleScrollEnd, updateCurrentVisibility]);

    useEffect(() => {
        const onRateChange = () => setPlaybackRate(audio?.playbackRate || 1);
        onRateChange();
        audio?.addEventListener('ratechange', onRateChange);
        return () => audio?.removeEventListener('ratechange', onRateChange);
    }, [audio]);

    useLayoutEffect(() => {
        following.current = true;
    }, [track?.id]);

    useLayoutEffect(() => {
        clearTimeout(scrollTimer.current);
        programmaticScroll.current = false;
        if (following.current) scrollToCurrent(false);
        updateCurrentVisibility();
    }, [scrollToCurrent, updateCurrentVisibility, track?.id]);

    useLayoutEffect(() => {
        const anchor = summaryAnchor.current;
        if (anchor) {
            const container = sectionsContainerRef.current;
            programmaticScroll.current = true;
            container.scrollTop += anchor.row.getBoundingClientRect().top - anchor.top;
            summaryAnchor.current = null;
            scheduleScrollEnd();
        }
        updateCurrentVisibility();
    }, [showSummary, sectionsContainerRef, scheduleScrollEnd, updateCurrentVisibility]);

    useEffect(() => {
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateCurrentVisibility);
        const container = sectionsContainerRef.current;
        if (container) observer?.observe(container);
        const row = rows.current[currentSection];
        if (row) observer?.observe(row);
        window.addEventListener('resize', updateCurrentVisibility);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', updateCurrentVisibility);
        };
    }, [currentSection, sectionsContainerRef, updateCurrentVisibility]);

    useEffect(() => () => clearTimeout(scrollTimer.current), []);

    const detachFromCurrent = () => {
        clearTimeout(scrollTimer.current);
        following.current = false;
        programmaticScroll.current = false;
    };

    const toggleSummaries = () => {
        const container = sectionsContainerRef.current;
        if (container?.scrollTop > 0) {
            const top = container.getBoundingClientRect().top;
            const row = rows.current.find(element => element?.getBoundingClientRect().bottom > top);
            if (row) summaryAnchor.current = { row, top: row.getBoundingClientRect().top };
        }
        setShowSummary(value => !value);
    };

    const playSection = (section) => {
        onSeek(section.startTime);
        eventLog(8, { trackId: track.id, sectionStartIndex: section.startIndex, topicIds: section.topics });
    };

    const shareSection = async (section) => {
        const url = `https://${LABEL_DOMAIN}/track/${track.fullTrackId ?? track.id}?start=${Math.floor(section.startTime)}`;
        setShareError('');
        setCopiedToastGeneration(0);
        try {
            logEvent(getAnalytics(), 'section_share');
            const result = await shareContent({ url });
            if (result === 'copied') setCopiedToastGeneration(generation => generation + 1);
        } catch (_) {
            setShareError(SHARE_FAILURE_MESSAGE);
        }
    };

    return (
        <section className='sectionsPanel' aria-labelledby='sectionsHeading'>
            <StatusToast open={copiedToastGeneration > 0} instanceKey={copiedToastGeneration}
                message='Copied to clipboard' onClose={() => setCopiedToastGeneration(0)} />
            <StatusToast tone='error' open={Boolean(shareError)} message={shareError} onClose={() => setShareError('')} />
            <header className='sectionsToolbar'>
                <h2 id='sectionsHeading'>Sections</h2>
                <button type='button' className='sectionsSummaryToggle'
                    aria-pressed={showSummary} title={showSummary ? 'Hide summaries' : 'Show summaries'} onClick={toggleSummaries}>
                    {showSummary ? <CheckRounded aria-hidden='true' /> : <FormatAlignLeftRounded aria-hidden='true' />}
                    <span>Summaries</span>
                </button>
                <button type='button' className='sectionsIconButton sectionsClose' aria-label='Close sections' onClick={close}>
                    <Close />
                </button>
            </header>
            <div className='sectionsScrollArea'>
                <div className='sectionsScroll' ref={sectionsContainerRef}
                    onWheel={detachFromCurrent} onTouchMove={detachFromCurrent}
                    onKeyDown={event => {
                        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(event.key)) detachFromCurrent();
                    }}
                    onScroll={() => {
                        if (programmaticScroll.current) scheduleScrollEnd();
                        else following.current = false;
                        updateCurrentVisibility();
                    }}>
                    <p className='sectionsGeneratedNote'>Sections automatically generated</p>
                    <ol className='sectionsList'>
                        {sections.map((section, index) => {
                            const isCurrent = currentSection === index;
                            const isPopular = popularSections.has(index);
                            const title = getSectionTitle(section);
                            const end = sections[index + 1]?.startTime ?? track.totalTimeSeconds;
                            const duration = Math.max(0, end - section.startTime);
                            const progress = duration > 0 ? Math.max(0, Math.min(1, (curTime - section.startTime) / duration)) : 0;
                            let timeLabel = formatSectionDuration(duration / playbackRate);
                            if (isCurrent) {
                                timeLabel = curTime < section.startTime
                                    ? `Starts in ${Math.ceil((section.startTime - curTime) / playbackRate)}s`
                                    : `${fancyTimeFormat(Math.max(0, end - curTime) / playbackRate)} left`;
                            }
                            return (
                                <li key={index} ref={element => { rows.current[index] = element; }}
                                    className={`sectionsRow${isCurrent ? ' isCurrent' : ''}${isPopular ? ' isPopular' : ''}${index + 1 === currentSection ? ' beforeCurrent' : ''}`}
                                    aria-current={isCurrent ? 'true' : undefined}>
                                    {isPopular && <span className='sectionsPopular' id={`${descriptionPrefix}-${index}-popular`}>
                                        <StarRounded aria-hidden='true' />Popular
                                    </span>}
                                    <button type='button' className={`sectionsNumber${isCurrent ? ' isCurrent' : ''}`}
                                        aria-label={isCurrent ? `${playing ? 'Pause' : 'Resume'} section ${index + 1}` : `Play section ${index + 1}`}
                                        onClick={isCurrent ? onTogglePlayback : () => playSection(section)}>
                                        {isCurrent ? <span className='sectionsProgressRing' style={{ '--section-progress': `${progress * 360}deg` }}>
                                            <span className='sectionsPlaybackIcon'>{playing ? <Pause /> : <PlayArrow />}</span>
                                        </span> : index + 1}
                                    </button>
                                    <button type='button' className='sectionsRowMain' onClick={() => playSection(section)}
                                        aria-label={`Play section ${index + 1} from start: ${title}`}
                                        aria-describedby={`${isPopular ? `${descriptionPrefix}-${index}-popular ` : ''}${descriptionPrefix}-${index}`}>
                                        <span className='sectionsTitle'>{title}</span>
                                        <span id={`${descriptionPrefix}-${index}`}>
                                            {showSummary && section.summary && <span className='sectionsSummary'>{section.summary}</span>}
                                            <span className='sectionsMetadata'>
                                                <span className='sectionsTime'>{timeLabel}</span>
                                            </span>
                                        </span>
                                    </button>
                                    <button type='button' className='sectionsIconButton sectionsShare' aria-label={`Share section ${index + 1}`}
                                        onClick={() => shareSection(section)}><IosShareRounded /></button>
                                </li>
                            );
                        })}
                    </ol>
                </div>
                {showCurrentSectionButton && <div className='sectionsCurrentControl'>
                    <FullWidthButton Icon={SwapVert} title='Current section' showChevron={false}
                        onClick={() => { following.current = true; scrollToCurrent(); }}
                        iconStyle={{ fontSize: '18px', verticalAlign: 'middle' }}
                        containerStyle={{ width: 'auto' }} buttonStyle={{ width: 'auto' }}
                        buttonClassName='previewFloatingButton previewAutoScrollButton'
                        iconShellClassName='previewFloatingButtonIconShell' textClassName='previewFloatingButtonText' />
                </div>}
            </div>
        </section>
    );
};

export default Sections;
